// TiersPage — Compare tiers, see locked features, upgrade
// Layout:
//   1. Top nav: < Back | "Plans & Tiers"
//   2. Hero showing current tier
//   3. Comparison table (Beginner / Mid / Professional) with checkmarks
//   4. Upgrade CTA per tier

import { useNavigate } from 'react-router-dom';
import { TIERS, TIER_ORDER, TIER_COMPARISON, type TierKey, DEMO_CHALLENGE } from '../../data/platforms';
import { TierPill } from '../../components/Brand';

const TIER_PRICING: Record<TierKey, { monthly: number; oneTime: number }> = {
  Beginner:  { monthly: 0,    oneTime: 0   },
  Mid:       { monthly: 29,   oneTime: 99  },
  Professional: { monthly: 79, oneTime: 199 },
};

export function TiersPage() {
  const nav = useNavigate();
  const userTier: TierKey = DEMO_CHALLENGE.tier;
  const tierOrder: Record<TierKey, number> = { Beginner: 0, Mid: 1, Professional: 2 };
  const userTierIdx = tierOrder[userTier];

  return (
    <div className="min-h-screen bg-white pb-8">
      {/* Top nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Plans & Tiers</div>
        <div className="w-9" />
      </div>

      {/* Current tier hero */}
      <div className="px-4 pt-5">
        <h1 className="text-[28px] font-bold tracking-tight text-tf-text-1">Choose the right plan</h1>
        <p className="mt-1 text-[14px] text-tf-text-3">Unlock more platforms, AI bot strategies, and higher profit splits.</p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[13px] text-tf-text-3">Your current plan:</span>
          <TierPill tier={userTier} />
        </div>
      </div>

      {/* Tier cards */}
      <div className="px-4 mt-4 space-y-3">
        {TIER_ORDER.map((tKey) => {
          const tier = TIERS[tKey];
          const isCurrent = tKey === userTier;
          const isUpgrade = tierOrder[tKey] > userTierIdx;
          const pricing = TIER_PRICING[tKey];
          return (
            <div
              key={tKey}
              data-testid={`tier-card-${tKey.toLowerCase()}`}
              className={['relative overflow-hidden rounded-[20px] border-2 p-4 transition',
                isCurrent ? 'border-tf-blue bg-tf-blue-tint' : 'border-tf-border bg-white'].join(' ')}
            >
              {tier.badge && (
                <div className="absolute right-4 top-4">
                  <span className="rounded-full bg-tf-warning px-2 py-0.5 text-[10px] font-semibold uppercase text-white">{tier.badge}</span>
                </div>
              )}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <TierPill tier={tKey} />
                    {isCurrent && <span className="rounded-full bg-tf-blue px-2 py-0.5 text-[10px] font-semibold text-white">CURRENT</span>}
                  </div>
                  <div className="mt-1 text-[14px] text-tf-text-3">{tier.tagline}</div>
                </div>
              </div>

              <div className="mt-3 flex items-end gap-2">
                {pricing.monthly === 0 ? (
                  <div className="text-[28px] font-bold tracking-tight text-tf-text-1">Free</div>
                ) : (
                  <>
                    <div className="text-[28px] font-bold tracking-tight text-tf-text-1">${pricing.monthly}</div>
                    <span className="text-[14px] text-tf-text-3 mb-1">/mo</span>
                  </>
                )}
                {pricing.oneTime > 0 && (
                  <div className="text-[12px] text-tf-text-3 mb-1.5">or ${pricing.oneTime} one-time</div>
                )}
              </div>

              <ul className="mt-3 space-y-1.5">
                <Bullet text={`Up to ${tier.profitSplit}% profit split`} />
                <Bullet text={`${tier.fundedDaysMin} funded days minimum`} />
                <Bullet text={`${tier.maxActiveChallenges} active challenge${tier.maxActiveChallenges > 1 ? 's' : ''}`} />
                <Bullet text={`${tier.botStrategiesAvailable} of 4 AI bot strategies`} />
                <Bullet text={`${tier.maxBotsAtOnce} bot${tier.maxBotsAtOnce > 1 ? 's' : ''} at once`} />
                {tier.hasCustomRisk && <Bullet text="Custom risk per position" />}
                {tier.hasPrioritySupport && <Bullet text="Priority support" />}
              </ul>

              {isUpgrade && (
                <button
                  onClick={() => nav('/portal/checkout/upgrade/' + tKey)}
                  data-testid={`upgrade-${tKey.toLowerCase()}`}
                  className="mt-4 flex h-[44px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.99]"
                >
                  Upgrade to {tKey}
                </button>
              )}
              {isCurrent && (
                <div className="mt-4 flex h-[44px] w-full items-center justify-center rounded-full bg-tf-blue-tint text-[15px] font-semibold text-tf-blue">
                  Your current plan
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Feature comparison table */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Feature comparison</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 border-b border-tf-border bg-tf-surface-1 px-3 py-2 text-[12px] font-semibold text-tf-text-3">
          <span>Feature</span>
          <span className="text-center">Beginner</span>
          <span className="text-center">Mid</span>
          <span className="text-center">Pro</span>
        </div>
        {TIER_COMPARISON.map((row, i) => (
          <div key={i} className={['grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 px-3 py-3 text-[13px]',
            i < TIER_COMPARISON.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <span className="text-tf-text-1">{row.name}</span>
            <ValueCell value={row.beginner} />
            <ValueCell value={row.mid} />
            <ValueCell value={row.professional} highlight={userTier === 'Professional'} />
          </div>
        ))}
      </div>

      <p className="mt-4 px-4 text-center text-[11px] text-tf-text-3">
        You can downgrade or cancel anytime. All plans include EOD Balance drawdown and no activation fee.
      </p>
    </div>
  );
}

function Bullet({ text }: { text: string }) {
  return (
    <li className="flex items-center gap-2 text-[13px] text-tf-text-1">
      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-tf-profit" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      {text}
    </li>
  );
}

function ValueCell({ value, highlight }: { value: string | boolean; highlight?: boolean }) {
  if (value === true) {
    return <span className="flex justify-center"><svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-profit" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg></span>;
  }
  if (value === false) {
    return <span className="flex justify-center text-tf-text-muted">—</span>;
  }
  return <span className={['text-center text-[13px] font-medium', highlight ? 'text-tf-text-1' : 'text-tf-text-1'].join(' ')}>{value}</span>;
}
